// ═══════════════════════════════════════════════════════════════
//  Budget vs Actual
//  • Waterfall variance chart by department
//  • Category-level drill-down
//  • Variance heatmap (dept × month)
//  • AG Grid with drill-down + inline status
// ═══════════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Cell, ReferenceLine,
} from "recharts";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import WaterfallChart  from "../common/WaterfallChart";
import HeatmapGrid     from "../common/HeatmapGrid";
import LoaderComponent from "../../../../libraries/loader/loader";
import { PBExecuteQuery, Q } from "../../../../services/process/PlanningBudgeting/planningBudgetingService";
import jsPDF from "jspdf";
import "jspdf-autotable";
import excelIcon from "../../../../assets/images/excel.png";

const SECTION = ({ title, children }) => (
  <div style={{
    background: "#fff", borderRadius: 14,
    boxShadow: "0 2px 14px rgba(73,105,171,0.09)",
    padding: "18px 20px", marginBottom: 22,
  }}>
    <div style={{ fontWeight: 700, fontSize: 15, color: "#1a2340", marginBottom: 14 }}>{title}</div>
    {children}
  </div>
);

const VarianceBadge = ({ pct }) => {
  const v = Number(pct);
  const color = v >= 5 ? "#2d8c4e" : v >= 0 ? "#27ae60" : v >= -10 ? "#e67e22" : "#c0392b";
  const label = v >= 5 ? "Favourable" : v >= 0 ? "On Track" : v >= -10 ? "At Risk" : "Over Budget";
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
      background: `${color}18`, color,
    }}>{label} ({v.toFixed(1)}%)</span>
  );
};

export default function BudgetVsActual() {
  const [year,     setYear]    = useState(new Date().getFullYear());
  const [tab,      setTab]     = useState("waterfall");
  const [varData,  setVarData] = useState([]);
  const [hmData,   setHmData]  = useState([]);
  const [loading,  setLoading] = useState(false);
  const [qf,       setQf]      = useState("");
  const [selectedDept, setSelectedDept] = useState(null);
  const gridRef = useRef(null);

  const [userCredentials] = useState(
    JSON.parse(sessionStorage.getItem("userCredentials") || "{}")
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const c = PBExecuteQuery();
      const [varRes, hmRes] = await Promise.all([
        axios.post(c[1], { query: Q.budgetVsActual(userCredentials.selectedentity, year) }, c[0]),
        axios.post(c[1], { query: Q.deptSpendHeatmap(userCredentials.selectedentity, year) }, c[0]),
      ]);
      setVarData(varRes.data || []);
      setHmData(hmRes.data   || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [year, userCredentials.selectedentity]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Rollup by dept ──────────────────────────────────────────
  const deptMap = {};
  varData.forEach((r) => {
    if (!deptMap[r.department]) deptMap[r.department] = { budget: 0, actual: 0 };
    deptMap[r.department].budget += Number(r.budget || 0);
    deptMap[r.department].actual += Number(r.actual || 0);
  });
  const deptRollup = Object.entries(deptMap).map(([dept, v]) => ({
    category:    dept,
    budget:      v.budget,
    actual:      v.actual,
    variance:    v.budget - v.actual,
    variance_pct:v.budget ? (((v.budget - v.actual) / v.budget) * 100).toFixed(1) : 0,
  })).sort((a, b) => a.variance - b.variance);

  // ── Category drill-down for selected dept ──────────────────
  const drillData = varData
    .filter((r) => !selectedDept || r.department === selectedDept)
    .map((r) => ({
      name:    r.expense_category,
      Budget:  Number(r.budget  || 0),
      Actual:  Number(r.actual  || 0),
      variance:Number(r.variance || 0),
    }));

  // ── Summary ────────────────────────────────────────────────
  const totalBudget   = varData.reduce((s, r) => s + Number(r.budget   || 0), 0);
  const totalActual   = varData.reduce((s, r) => s + Number(r.actual   || 0), 0);
  const totalVariance = totalBudget - totalActual;
  const variancePct   = totalBudget ? ((totalVariance / totalBudget) * 100).toFixed(1) : 0;

  // ── Exports ─────────────────────────────────────────────────
  const exportCSV = () => gridRef.current?.api?.exportDataAsCsv();
  const exportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a3" });
    doc.setFontSize(14);
    doc.text(`Budget vs Actual – FY ${year}`, 210, 12, { align: "center" });
    doc.autoTable({
      head: [["Department","Category","Budget","Actual","Variance","Variance %"]],
      body: varData.map((r) => [
        r.department, r.expense_category,
        `₹${Number(r.budget||0).toLocaleString("en-IN")}`,
        `₹${Number(r.actual||0).toLocaleString("en-IN")}`,
        `₹${Number(r.variance||0).toLocaleString("en-IN")}`,
        `${Number(r.variance_pct||0).toFixed(1)}%`,
      ]),
      startY: 20,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [73, 105, 171] },
    });
    doc.save(`BudgetVsActual_FY${year}.pdf`);
  };

  const COL_DEFS = [
    { headerName: "Department",   field: "department",    sortable: true, filter: true, resizable: true, pinned: "left", width: 160,
      cellRenderer: (p) => (
        <span style={{ cursor: "pointer", color: "#4969ab", fontWeight: 600 }}
              onClick={() => setSelectedDept(p.value === selectedDept ? null : p.value)}>
          {p.value}
        </span>
      ),
    },
    { headerName: "Category",     field: "expense_category", sortable: true, filter: true, resizable: true, width: 160 },
    { headerName: "Budget (₹)",   field: "budget",  sortable: true, filter: true, width: 140,
      cellRenderer: (p) => `₹${Number(p.value||0).toLocaleString("en-IN")}` },
    { headerName: "Actual (₹)",   field: "actual",  sortable: true, filter: true, width: 140,
      cellRenderer: (p) => `₹${Number(p.value||0).toLocaleString("en-IN")}` },
    { headerName: "Variance (₹)", field: "variance",sortable: true, filter: true, width: 140,
      cellStyle: (p) => ({ color: Number(p.value) >= 0 ? "#2d8c4e" : "#c0392b", fontWeight: 700 }),
      cellRenderer: (p) => `${Number(p.value) >= 0 ? "▲" : "▼"} ₹${Math.abs(Number(p.value||0)).toLocaleString("en-IN")}` },
    { headerName: "Status",       field: "variance_pct", sortable: true, filter: true, width: 160,
      cellRendererFramework: (p) => <VarianceBadge pct={p.value} /> },
  ];

  if (loading) return <LoaderComponent loaderOpen />;

  return (
    <div style={{ padding: "4px 2px" }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: "#1a2340", marginBottom: 4 }}>Budget vs Actual</div>
      <div style={{ fontSize: 12, color: "#aaa", marginBottom: 18 }}>
        Variance analysis · waterfall · department drill-down · FY {year}
      </div>

      {/* Header controls */}
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 18 }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {[
            { label: "Total Budget",   value: `₹${(totalBudget/10000000).toFixed(2)}Cr`,   color: "#4969ab" },
            { label: "Total Actual",   value: `₹${(totalActual/10000000).toFixed(2)}Cr`,   color: "#c0392b" },
            { label: "Total Variance", value: `₹${(Math.abs(totalVariance)/10000000).toFixed(2)}Cr`, color: totalVariance >= 0 ? "#2d8c4e" : "#c0392b" },
            { label: "Variance %",     value: `${variancePct}%`, color: Number(variancePct) >= 0 ? "#2d8c4e" : "#c0392b" },
          ].map(({ label, value, color }) => (
            <div key={label} style={{
              background: `${color}10`, border: `1px solid ${color}30`,
              borderRadius: 10, padding: "8px 14px",
            }}>
              <div style={{ fontSize: 10, color: "#999", textTransform: "uppercase" }}>{label}</div>
              <div style={{ fontWeight: 800, fontSize: 16, color }}>{value}</div>
            </div>
          ))}
        </div>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={{
          padding: "6px 12px", borderRadius: 8, border: "1px solid #d0d8ec", fontSize: 13,
        }}>
          {[2023,2024,2025,2026].map((y) => <option key={y}>{y}</option>)}
        </select>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 0, marginBottom: 22, borderBottom: "2px solid #e8ecf4" }}>
        {[
          ["waterfall","📉 Waterfall"],
          ["drilldown","🔍 Category Drill-Down"],
          ["heatmap",  "🌡️ Heatmap"],
          ["grid",     "📋 Full Data"],
        ].map(([k, lbl]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding: "8px 18px", border: "none", cursor: "pointer",
            background: tab === k ? "#4969ab" : "transparent",
            color: tab === k ? "#fff" : "#666",
            fontWeight: tab === k ? 700 : 400,
            fontSize: 13, borderRadius: "8px 8px 0 0", transition: "all 0.15s",
          }}>{lbl}</button>
        ))}
      </div>

      {/* ══ Waterfall ══ */}
      {tab === "waterfall" && (
        deptRollup.length > 0
          ? <WaterfallChart data={deptRollup} title="Budget vs Actual Variance – by Department" height={380} />
          : <div style={{ textAlign: "center", color: "#bbb", padding: 60 }}>No data for FY {year}</div>
      )}

      {/* ══ Drill-down bar ══ */}
      {tab === "drilldown" && (
        <SECTION title={`Category Breakdown ${selectedDept ? `— ${selectedDept}` : "(All Departments)"}`}>
          {selectedDept && (
            <button onClick={() => setSelectedDept(null)} style={{
              marginBottom: 12, padding: "4px 12px", borderRadius: 8,
              border: "1px solid #d0d8ec", background: "#f5f7fb",
              fontSize: 11, cursor: "pointer", color: "#666",
            }}>✕ Clear filter</button>
          )}
          {drillData.length > 0 ? (
            <ResponsiveContainer width="100%" height={380}>
              <ComposedChart data={drillData} margin={{ top: 5, right: 20, left: 10, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f8" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" interval={0} />
                <YAxis tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`} />
                <Legend iconSize={9} wrapperStyle={{ fontSize: 11 }} />
                <ReferenceLine y={0} stroke="#ccc" />
                <Bar dataKey="Budget" fill="#4969ab" radius={[4,4,0,0]} />
                <Bar dataKey="Actual" fill="#2d8c4e" radius={[4,4,0,0]} />
                <Line type="monotone" dataKey="variance" stroke="#e67e22" strokeWidth={2} dot={{ r: 3 }} name="Variance" />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ textAlign: "center", color: "#bbb", padding: 40 }}>No data</div>
          )}
          <div style={{ fontSize: 11, color: "#aaa", marginTop: 8 }}>
            💡 Click a department name in the Full Data tab to filter categories.
          </div>
        </SECTION>
      )}

      {/* ══ Heatmap ══ */}
      {tab === "heatmap" && (
        hmData.length > 0
          ? <HeatmapGrid data={hmData} title={`Department Spend Utilization Heatmap — FY ${year}`} />
          : <div style={{ textAlign: "center", color: "#bbb", padding: 60 }}>No heatmap data</div>
      )}

      {/* ══ Grid ══ */}
      {tab === "grid" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ display: "flex", gap: 8 }}>
              <img src={excelIcon} alt="CSV" title="Export CSV" onClick={exportCSV}
                style={{ width: 28, height: 28, cursor: "pointer" }} />
              <button onClick={exportPDF} style={{
                padding: "4px 10px", borderRadius: 6, border: "1px solid #d9534f",
                color: "#d9534f", background: "#fff", fontSize: 11, cursor: "pointer",
              }}>PDF</button>
            </div>
            <input type="text" placeholder="Search..."
              value={qf} onChange={(e) => { setQf(e.target.value); gridRef.current?.api?.setQuickFilter(e.target.value); }}
              style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #d0d8ec", fontSize: 12, width: 260 }} />
          </div>
          <div className="ag-theme-alpine" style={{ height: 480, width: "100%", borderRadius: 8 }}>
            <AgGridReact
              ref={gridRef}
              rowData={varData}
              columnDefs={COL_DEFS}
              pagination paginationPageSize={12}
              defaultColDef={{ sortable: true, filter: true, resizable: true }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
